import ApiClient from '../apiClient';
export interface PartnerOffer {
  id: string;
  created_at: string;
  updated_at?: string;
  /** Тип оффера: 'cpl' — оплата за подписчика, 'subscription' — месячная подписка. */
  type?: 'cpl' | 'subscription';
  source:
    | 'only_fans'
    | 'reddit'
    | 'instagram'
    | 'tiktok'
    | 'google'
    | 'telegram'
    | 'twitter'
    | 'youtube'
    | 'another';
  description: string;
  requirements: string;
  status: 'active' | 'hidden' | 'moderation' | 'hide';
  subs: Array<{
    /** У офферов типа 'subscription' ступень отсутствует. */
    ge?: number;
    price: string;
  }>;
  cpl_orders_count_by_status: {
    in_progress: number;
    waiting: number;
    canceled: number;
    completed: number;
  };
  subs_pre_day: number;
  /** Не возвращается для офферов типа 'subscription'. */
  subs_min?: number;
  /** Не возвращается для офферов типа 'subscription'. */
  subs_max?: number;
  active_campaigns: number;
  campaign_types: string[];
  show_romi: boolean;
  other_source_name: string | null;
  of_models: number[] | null;
  owner:
    | string
    | {
        name: string;
        email?: string;
        avatar?: string;
        averageRating: number;
      };
  queue: string[];
}

export interface PartnerCampaign {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'paused' | 'completed';
  created_at: string;
  offers_count: number;
  total_revenue: number;
}

export interface PartnerRequestParams {
  search?: string | null;
  status?: 'active' | 'hidden' | 'moderation' | 'hide' | null;
  source?:
    | 'only_fans'
    | 'reddit'
    | 'instagram'
    | 'tiktok'
    | 'google'
    | 'telegram'
    | 'twitter'
    | 'youtube'
    | 'another'
    | null;
  sort?: string | null;
  page?: number;
  page_size?: number;
  price_min?: number | null;
  price_max?: number | null;
}

export interface PartnerOrdersRequestParams {
  search?: string | null;
  status?: 'in_progress' | 'waiting' | 'canceled' | 'completed' | null;
  offer?: string | null;
  page?: number;
  page_size?: number;
}

export interface PartnerOrder {
  id: string;
  created_at: string;
  subs: {
    count: number;
    price: string;
    with_gte_3_chat_messages_count: number | null;
  };
  model: {
    id: string;
    type: 'free' | 'paid' | 'free trial';
    link: string;
    track_link_id: number;
    of_user_name: string;
    of_user_id: number;
    avatar: string;
    overall_popularity_percent: number;
    transitions_count: number;
    blocked_countries: string[];
    public_content: string[] | null;
    work_content: Record<string, string> | null;
  };
  offer: {
    id: string;
    partner: {
      id: string;
      name: string;
      avatar: string | null;
      cpl_statistic: any;
    };
    source:
      | 'only_fans'
      | 'reddit'
      | 'instagram'
      | 'tiktok'
      | 'google'
      | 'telegram'
      | 'twitter'
      | 'youtube'
      | 'another';
    description: string;
    requirements: string;
  };
  access_work_media: any;
  subscribe_days: number;
  commition: number;
  total_price: number;
  owner: string;
  status: 'in_progress' | 'waiting' | 'canceled' | 'completed';
  subscribed_count: number | null;
  transactions_statistic: {
    [key: string]: number;
    total: number;
  };
}

export interface PartnerOrdersApiResponse {
  page: number;
  page_size: number;
  count: number;
  objects: PartnerOrder[];
}

export interface PartnerApiResponse {
  page: number;
  page_size: number;
  count: number;
  objects: PartnerOffer[];
}

export interface PublicLink {
  id: string;
  name?: string;
  updated_at: string;
  created_at: string;
  campaigns: number[];
  income: boolean;
  revenue: boolean;
  costs: boolean;
  active: boolean;
  owner: string;
}

export interface PublicLinkCreateRequest {
  campaigns: number[];
  income: boolean;
  revenue: boolean;
  costs: boolean;
  active: boolean;
}

export interface PublicLinksApiResponse {
  page: number;
  page_size: number;
  count: number;
  objects: PublicLink[];
}

export interface PublicLinkData {
  created_at: string;
  _id: string;
  subscribed_count: number | null;
  transactions_statistic?: {
    [key: string]: number | string;
    total?: string;
    count?: number;
  };
  subs: {
    count: number;
    price: string;
    with_gte_3_chat_messages_count: number | null;
  };
  commition: number;
  total_price: number;
  model: {
    id: string;
    type: 'free' | 'paid';
    link: string;
    of_user_name: string;
    avatar: string;
    track_link_id: number;
    of_user_id: number;
    of_data: {
      email: string;
      subscribers: number;
      overall_popularity_percent: number;
      updated_at: string;
      balance: string;
      favorited: number;
      posts: number;
      blocked_countries: string[];
    };
  };
  source: string;
  status: 'in_progress' | 'waiting' | 'canceled' | 'completed';
  transitions_count: number;
}

export interface PublicLinkApiResponse {
  page: number;
  page_size: number;
  count: number;
  objects: PublicLinkData[];
}

const partnerOfferService = {
  getOffers: (params: PartnerRequestParams = {}): Promise<PartnerApiResponse> => {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.append('status', params.status);
    if (params.source) searchParams.append('source', params.source);
    if (params.sort) searchParams.append('sort_by', params.sort);
    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());
    return ApiClient.get(`/partners/cpl/offers?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },
  getOtherOffers: (params: PartnerRequestParams = {}): Promise<PartnerApiResponse> => {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.append('status', params.status);
    if (params.source) searchParams.append('source', params.source);
    if (params.sort) searchParams.append('sort_by', params.sort);
    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());
    return ApiClient.get(`/partners/cpl/offers/free?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },
  createOffer: (data: any) => ApiClient.post(`/partners/cpl/offers`, data),
  getOfferById: (offerId: string) => ApiClient.get(`/partners/cpl/offers/${offerId}`),
  updateOffer: (offerId: string, data: any) =>
    ApiClient.patch(`/partners/cpl/offers/${offerId}`, data),
  deleteOffer: (offerId: string) => ApiClient.delete(`/partners/cpl/offers/${offerId}`),
  getOrders: (params: PartnerOrdersRequestParams = {}): Promise<PartnerOrdersApiResponse> => {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.append('search', params.search);
    if (params.status) searchParams.append('status', params.status);
    if (params.offer) searchParams.append('offer', params.offer);
    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());
    return ApiClient.get(`/partners/cpl/orders?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },

  acceptOrder: (orderId: string) =>
    ApiClient.post(`/partners/cpl/orders/${orderId}/accept`, {}).then((response) => response.data),

  cancelOrder: (orderId: string) =>
    ApiClient.delete(`/partners/cpl/orders/${orderId}`).then((response) => response.data),

  // Public Links
  getPublicLinks: (
    params: { page?: number; page_size?: number; sort_by?: string } = {}
  ): Promise<PublicLinksApiResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());
    if (params.sort_by) searchParams.append('sort_by', params.sort_by);
    return ApiClient.get(`/partners/public_link?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },

  createPublicLink: (data: PublicLinkCreateRequest) => {
    return ApiClient.post(`/partners/public_link`, data);
  },

  updatePublicLink: (linkId: string, data: PublicLinkCreateRequest) => {
    return ApiClient.patch(`/partners/public_link/${linkId}`, data);
  },

  deletePublicLink: (linkId: string) => {
    return ApiClient.delete(`/partners/public_link/${linkId}`);
  },

  addCampaignToPublicLink: (linkId: string, campaignId: number) => {
    return ApiClient.post(`/partners/public_link/${linkId}/campaigns/${campaignId}`, {});
  },

  removeCampaignFromPublicLink: (linkId: string, campaignId: number) => {
    return ApiClient.delete(`/partners/public_link/${linkId}/campaigns/${campaignId}`);
  },

  getPublicLinkData: (linkId: string): Promise<PublicLinkApiResponse> => {
    return ApiClient.get(`/partners/public/${linkId}`).then((response) => response.data);
  },
};

export default partnerOfferService;
